import React, { useContext, useEffect, useState } from 'react'
import { AppSettings } from '../../config/app-settings'
import { toast } from 'react-toastify'
import Loader from '../pages/loader'
import { useParams } from 'react-router-dom'
import { Card, CardBody, CardHeader } from '../../components/other/card/card'
import { useGetIptvByIdQuery } from '../../redux/apis/linkManagement/iptv/index/lmIptvApiSlice'
import { useGetIptvMediaFilesListQuery } from '../../redux/apis/linkManagement/iptv/media/lmIptvFilesApiSlice'

const IptvDataInfo = React.memo(() => {
  const context = useContext(AppSettings)
  const { id } = useParams()
  const { data, isLoading, error } = useGetIptvByIdQuery({
    expand: ['last_updated_by', 'status'].join(','),
    id: id
  })

  const { data: mediaFiles, isLoading: isLoadingMedia } = useGetIptvMediaFilesListQuery(
    { iptv: id },
    { skip: !id }
  )

  useEffect(() => {
    if (error?.data) {
      const errorMessages = Object.values(error.data).flat()
      errorMessages.forEach(message => {
        toast.error(message)
      })
    }
  }, [error])

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context]) // Add context as a dependency

  return (
    <div className='w-full flex flex-col'>
      <Loader isLoading={isLoading} />
      <Card>
        <CardHeader>{data?.data?.app_name}</CardHeader>
        <CardBody>
          <table className='table table-striped'>
            <tbody>
              <tr>
                <th scope='row'>Software/Application Name</th>
                <td>{data?.data?.app_name}</td>
              </tr>
              <tr>
                <th scope='row'>Developer </th>
                <td>{data?.data?.developer}</td>
              </tr>
              <tr>
                <th scope='row'>Software/Application Version</th>
                <td>{data?.data?.version}</td>
              </tr>
              <tr>
                <th scope='row'>Subscription Username</th>
                <td>{data?.data?.subscription_username}</td>
              </tr>
              <tr>
                <th scope='row'>Subscription Password</th>
                <td>{data?.data?.subscription_password}</td>
              </tr>
              <tr>
                <th scope='row'>Subscription Code</th>
                <td>{data?.data?.subscription_code}</td>
              </tr>
              <tr>
                <th scope='row'>Subscription Expiration Date</th>
                <td>{data?.data?.subscription_expiry}</td>
              </tr>
              <tr>
                <th scope='row'>Status</th>
                <td>{data?.data?.status?.status_name}</td>
              </tr>
              <tr>
                <th scope='row'>Creation Date</th>
                <td>{data?.data?.created_at}</td>
              </tr>
              <tr>
                <th scope='row'>Last Updated By</th>
                <td>
                  {data?.data?.last_updated_by?.first_name}{' '}
                  {data?.data?.last_updated_by?.last_name}
                </td>
              </tr>
              <tr>
                <th scope='row'>Updated At</th>
                <td>{data?.data?.updated_at}</td>
              </tr>
            </tbody>
          </table>
        </CardBody>
      </Card>

      {data?.data?.app_logo_path && (
        <Card style={{ marginTop: '20px' }}>
          <CardHeader>App Logo</CardHeader>
          <CardBody>
            <a
              href={data.data.app_logo_path}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <img
                src={data.data.app_logo_path}
                alt={`${data.data.app_name || 'IPTV'} app logo`}
                style={{
                  width: '150px',
                  height: '150px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '2px solid #ddd',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              />
            </a>
          </CardBody>
        </Card>
      )}

      {mediaFiles?.data?.results?.length > 0 && (
        <Card style={{ marginTop: '20px' }}>
          <CardHeader>Media Files</CardHeader>
          <CardBody>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
              {mediaFiles.data.results.map((file) => {
                const format = file.file_format?.toLowerCase()
                const isImage = format === 'jpg' || format === 'jpeg' || format === 'png' ||
                               format === 'gif' || format === 'webp' || format === 'bmp'

                return (
                  <div key={file.id} style={{ position: 'relative' }}>
                    <a href={file.attachment} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                      {isImage ? (
                        <img
                          src={file.attachment}
                          alt="Media file"
                          style={{
                            width: '150px',
                            height: '150px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: '2px solid #ddd',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '150px',
                            height: '150px',
                            borderRadius: '8px',
                            border: '2px solid #ddd',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f8f9fa',
                            padding: '10px',
                            textAlign: 'center'
                          }}
                        >
                          <div style={{ fontSize: '40px', marginBottom: '10px', color: '#6c757d' }}>
                            📄
                          </div>
                          <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#495057', marginBottom: '5px', wordBreak: 'break-word' }}>
                            {file.attachment?.split('/').pop()?.substring(0, 20) || 'File'}
                            {file.attachment?.split('/').pop()?.length > 20 && '...'}
                          </div>
                          <div style={{ fontSize: '10px', color: '#6c757d', textTransform: 'uppercase' }}>
                            {file.file_format}
                          </div>
                        </div>
                      )}
                    </a>
                    <div style={{ fontSize: '12px', textAlign: 'center', marginTop: '6px', color: '#666' }}>
                      {(file.file_size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                )
              })}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  )
})

export default IptvDataInfo
